// Core types for price parts functionality
// Extracted from apps/web/src/components/product/types.ts and api.ts

export enum SortType {
  'ALPHABETICAL' = 'ALPHABETICAL',
  'MANUAL' = 'MANUAL',
}

export type ListType = 'products' | 'bundles' | 'productgroup';

export type KeyValue = {
  [key: string]: any;
};

export type ValueOption = {
  name: string;
  value: string;
  uuid?: string;
};

export type QuantityModifierOperand = {
  uuid?: string;
  name: string;
  displayName?: string;
  has_value_options?: boolean;
  useVariableName?: boolean;
  value_options: ValueOption[];
  value_predefined?: boolean;
  value?: string | null;
};

export type QuantityModifier = {
  name: string;
  uuid: string;
  unit: string;
  round_method: 'CEIL' | 'FLOOR' | '';
  operator: string | null;
  operator_uuid?: string;
  operator_name?: string;
  factor: string | number | undefined | null;
  expr: string;
  operands: QuantityModifierOperand[];
  is_built_in: boolean;
  output: { unit: string };
  order?: number;
  formula?: string;
};

export type Modifier = {
  name: string;
  uuid: string;
  output: { unit: string };
  round_method?: 'CEIL' | 'FLOOR' | '';
  operations: { operator: string; operand: { value: string; uuid: string } }[];
  expr: string;
  operands: QuantityModifierOperand[];
  is_built_in: boolean;
  formula?: string;
};

export type PriceOption = {
  use_custom_quantity: boolean;
  custom_quantity_unit: string | null;
  tiered_prices: KeyValue[] | null;
  price: number;
  use_minimum_price: boolean;
  use_tiered_price: boolean;
  minimum_price: number | null;
  minimum_cost: number | null;
  uuid: string;
  name: string;
  tiered_price_method: 'MULTIPLY' | 'ADD_UP';
  cost?: number | null; // Extended to include cost
  sku?: string | null;
};

export type PricePart = {
  mandatory: boolean;
  name: string;
  order: number;
  price_options: PriceOption[];
  optional: boolean;
  uuid: string;
};

export type ProductStyle = {
  properties: { name: string }[];
};

export type Field = {
  field_id: string;
  field_name: string;
  field_type: string;
  options: string[];
  order: number;
  is_item_field: boolean;
  field_value?: string;
  options_string?: string | null;
  mandatory?: boolean;
  is_linked_shape_label?: boolean;
  linked_shape_label?: string;
};

export type Product = {
  style: ProductStyle;
  category_uuid: string;
  id: string;
  product_fields: Field[];
  uuid: string;
  product_class: string;
  price: number;
  cost: number;
  unit: string;
  pricing_method: string; // Will be typed properly with constants
  product_name: string;
  product_description: string;
  thumbnail_url: string;
  amount: number;
  product_sku: string;
  product_quantity_modifiers_v2: QuantityModifier[];
  quantity_modifiers_v2: QuantityModifier[];
  price_parts: PricePart[];
  taxable: boolean;
  is_bundle_product?: boolean;
  product_uuid: string;
  block_resources: string[];
  fields_value: string[];
  currency: string;
  category_name?: string;
  product_id: string;
  version_id: string;
  shape_resource: {
    id: string;
    project_id: string;
  } | null;
  hide_in_app: boolean;
  hide_in_proposal: boolean;
  product_media: {
    images: ProductMediaImage[];
    videos: ProductMediaVideo[];
  };
};

export type ProductMediaBase = {
  media_id: string;
  is_primary: boolean;
  url: string;
  create_time: string;
};

export type ProductMediaImage = ProductMediaBase & {
  thumbnail_url: string;
};

export type ProductMediaVideo = ProductMediaBase;

export type ProductMedia = ProductMediaImage | ProductMediaVideo;

export type ConvertedProduct = Omit<
  Product,
  'quantity_modifiers' | 'product_quantity_modifiers' | 'product_media'
> & {
  product_quantity_modifiers: QuantityModifier[];
  quantity_modifiers: QuantityModifier[];
};

export type Category = {
  product_type: string;
  description: string | null;
  org_id: string;
  uuid: string;
  creator_email: string;
  id: string;
  category_name: string;
  create_time: string;
  products_uuid: string;
  product_fields?: Field[];
  bundle_sort_type: SortType;
  product_sort_type?: SortType;
};
